import { filterClasses } from "@swivl-mono/helpers";
import { TicketPreview, formatTicketNumberDisplay } from "@swivl-mono/types";
import { Button, ButtonVariant, SearchInput, useIsDesktop } from "@swivl-mono/ui";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiOutlineSearch } from "react-icons/hi";
import { useNavigate } from "react-router-dom";

import { useDebouncedValue } from "../../../Hooks/useDebouncedValue";
import { Member } from "../../../Models/Member/Member.Current";
import { CreatedTicketResult, useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";
import { CreateTicketActionSheet } from "./CreateTicketActionSheet";
import { CreateTicketModal } from "./CreateTicketModal";
import { InboxViewMode, TicketInboxNav } from "./TicketInboxNav";
import { TicketListControls } from "./TicketListControls";
import { TicketListItem } from "./TicketListItem";
import { useInboxFilterPersistence } from "./useInboxFilterPersistence";

const VIEW_TITLES: Record<InboxViewMode, string> = {
  my: "My Inbox",
  all: "All Tickets",
  group: "Group Inbox",
  search: "Search All"
};

export function TicketInbox() {
  const { rows, loading, error, selectedId, query, total, acceptanceOffset } = useTicketingStore((s) => ({
    rows: s.inboxRows,
    loading: s.inboxLoading,
    error: s.inboxError,
    selectedId: s.selectedTicketId,
    query: s.inboxQuery,
    total: s.inboxTotal,
    acceptanceOffset: s.inboxAcceptanceOffset
  }));

  const setQuery = useTicketingStore((s) => s.setInboxQuery);
  const refresh = useTicketingStore((s) => s.refreshInbox);
  const myGroups = useTicketingStore((s) => s.myGroups);
  const myGroupsLoaded = useTicketingStore((s) => s.myGroupsLoaded);
  const acceptTicketById = useTicketingStore((s) => s.acceptTicketById);
  const selectMyInboxNonce = useTicketingStore((s) => s.selectMyInboxNonce);
  const inboxViewRequest = useTicketingStore((s) => s.inboxViewRequest);
  const clearInboxViewRequest = useTicketingStore((s) => s.clearInboxViewRequest);

  // Loads persisted filters + applies the location-scope filter for
  // restricted members (listTickets only scopes by orgId server-side).
  useInboxFilterPersistence();

  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [viewMode, setViewMode] = useState<InboxViewMode>("my");
  const [activeGroupSlug, setActiveGroupSlug] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState(query.q ?? "");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const debouncedSearch = useDebouncedValue(searchText, 300);

  // Infinite scroll refs
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasMore = rows.length < total;

  // Stable key of my group slugs. `myGroups` loads asynchronously, so we re-run
  // the view query when the slug SET changes (not on every badge-count update)
  // — otherwise a fresh My Inbox refresh queries with no groups and misses the
  // pending "available to accept" tickets until you switch views.
  const myGroupSlugsKey = myGroups
    .map((g) => g.slug)
    .sort()
    .join(",");

  // Apply view mode filter when it changes
  useEffect(() => {
    // "my"     = My Inbox — tickets assigned to / claimed by me PLUS pending
    //            (open-to-group) tickets in my groups I can accept
    // "all"    = all tickets (no view filter)
    // "group"  = Group Inbox. With no active group it's the aggregate across all
    //            my groups; with one selected it's that single group's lane.
    //            Either way it shows both pending-acceptance and already-accepted
    //            tickets (pending sorted on top, server-side).
    // "search" = Search All — across every ticket (no group/assignee scope)
    //
    // "my" and "group" both derive their group-scoped filter from myGroups,
    // which loads asynchronously. Skip firing until it's resolved at least
    // once — otherwise the initial query goes out with an empty group filter
    // and silently under-populates "Available to accept" until myGroups
    // loads and this effect re-runs (ENG-2963).
    if ((viewMode === "my" || viewMode === "group") && !myGroupsLoaded) return;
    let myInbox: boolean | undefined;
    let routingGroupSlug: string | undefined;
    let routingGroupSlugs: string[] | undefined;
    const myGroupSlugs = myGroups.map((g) => g.slug);
    if (viewMode === "my") {
      myInbox = true;
      // My group slugs power the "available to accept in my groups" part of My
      // Inbox. refreshInbox splits this into two requests (ENG-2942) and sends
      // the slugs on the acceptance one only — keeping them on the "assigned to
      // me" request is what unioned both lanes into one paginated list and let
      // the "Assigned to me" section fall off page 1.
      routingGroupSlugs = myGroupSlugs;
    } else if (viewMode === "group") {
      if (activeGroupSlug) {
        routingGroupSlug = activeGroupSlug;
      } else if (myGroupSlugs.length === 0) {
        // No groups to aggregate — show nothing locally rather than send an
        // empty routingGroupSlugs, which the server reads as "no filter" and
        // would return every ticket in scope (ENG-2947). The nav hides this
        // entry point for a zero-group member; this is defense-in-depth for
        // any other path that lands here.
        useTicketingStore.setState({ inboxRows: [], inboxTotal: 0, inboxLoading: false, inboxError: null }, false, "ticketing/groupInbox:noGroups");
        return;
      } else {
        // Aggregate Group Inbox — all tickets across every group I belong to.
        routingGroupSlugs = myGroupSlugs;
      }
    }
    setQuery({ myInbox, assigneeMemberId: undefined, routingGroupSlug, routingGroupSlugs, openForAcceptance: undefined, offset: 0 });
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, activeGroupSlug, myGroupSlugsKey, myGroupsLoaded]);

  // Accepting a ticket from the global arrival toast requests My Inbox here
  // (the toast can't reach this view's local state). Skip the initial 0 value.
  useEffect(() => {
    if (selectMyInboxNonce === 0) return;
    setSearchOpen(false);
    setSearchText("");
    setActiveGroupSlug(null);
    setViewMode("my");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectMyInboxNonce]);

  // The Search All page's nav requests All Tickets / a group here (it can't
  // reach this view's local state). Consume-and-clear so a stale request doesn't
  // re-fire on remount (which would override a later My Inbox selection).
  useEffect(() => {
    if (!inboxViewRequest) return;
    setSearchOpen(false);
    setSearchText("");
    if (inboxViewRequest.mode === "group") {
      setActiveGroupSlug(inboxViewRequest.slug ?? null);
      setViewMode("group");
    } else {
      setActiveGroupSlug(null);
      setViewMode("all");
    }
    clearInboxViewRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxViewRequest?.nonce]);

  const handleSelectGroup = (slug: string): void => {
    setSearchOpen(false);
    setSearchText("");
    setActiveGroupSlug(slug);
    setViewMode("group");
  };

  const handleSelectView = (mode: "my" | "all"): void => {
    setSearchOpen(false);
    setSearchText("");
    setViewMode(mode);
    setActiveGroupSlug(null);
  };

  // Aggregate Group Inbox — all groups I belong to, no single-group filter.
  const handleSelectGroupInbox = (): void => {
    setSearchOpen(false);
    setSearchText("");
    setActiveGroupSlug(null);
    setViewMode("group");
  };

  // After creating a ticket, move the nav to wherever it actually landed so it
  // isn't "missing" from the current view: My Inbox if it's mine, otherwise its
  // group inbox (e.g. an Emergency ticket auto-assigned to someone else).
  const handleTicketCreated = (ticket: CreatedTicketResult): void => {
    const meId = Member.id;
    const mine = ticket.claimedByMemberId === meId || (meId != null && ticket.assigneeMemberIds.includes(meId));
    setSearchOpen(false);
    setSearchText("");
    if (mine) {
      setActiveGroupSlug(null);
      setViewMode("my");
    } else if (ticket.routingGroupSlug) {
      setActiveGroupSlug(ticket.routingGroupSlug);
      setViewMode("group");
    }
  };

  // "Search All" is now its own full-width page (route). Navigating there keeps
  // the nav's "Search All" highlighted via the route branch in TicketingPage.
  const handleSearchAll = (): void => {
    navigate("/inbox/search");
  };

  // Accepting a group ticket claims it — it's now mine. Switch to My Inbox,
  // select it, and open it. Only on a successful claim (a race loser stays put
  // and gets an alert from the store).
  const handleAcceptFromList = async (ticket: TicketPreview): Promise<void> => {
    const claimed = await acceptTicketById(ticket.id, ticket.version ?? 0);
    if (!claimed) return;
    setSearchOpen(false);
    setSearchText("");
    setActiveGroupSlug(null);
    setViewMode("my");
    navigate(`/inbox/ticket/${formatTicketNumberDisplay(ticket.number)}`);
  };

  useEffect(() => {
    setQuery({ q: debouncedSearch || undefined, offset: 0 });
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Infinite scroll with IntersectionObserver
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      // My Inbox paginates the "available to accept" lane only — assigned
      // tickets load in full up front — so its next page starts at that lane's
      // own cursor. `rows.length` counts both sections, which skipped one
      // acceptable ticket per assigned row and left the list stuck below its
      // own total (ENG-2942). Every other view is a single list, where
      // `rows.length` is the correct offset.
      setQuery({ offset: query.myInbox ? acceptanceOffset : rows.length });
      void refresh();
    }
  }, [loading, hasMore, rows.length, query.myInbox, acceptanceOffset, setQuery, refresh]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, root: scrollContainerRef.current }
    );

    const sentinel = loadMoreRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [loadMore]);

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchText("");
    // Leaving the Search All view returns to All Tickets.
    if (viewMode === "search") {
      setViewMode("all");
      setActiveGroupSlug(null);
    }
  };

  // "my" and "group" hold off firing their query until myGroups resolves
  // (see the view-filter effect above) — treat that wait as loading too, so
  // the empty state doesn't flash before the real query fires.
  const awaitingMyGroups = (viewMode === "my" || viewMode === "group") && !myGroupsLoaded;

  const statusFilterActive = (query.statuses?.length ?? 0) > 0;

  const clearStatusFilter = () => {
    setQuery({ statuses: undefined, offset: 0 });
    void refresh();
  };

  const activeTitle =
    viewMode === "group"
      ? activeGroupSlug
        ? (myGroups.find((g) => g.slug === activeGroupSlug)?.name ?? "Group")
        : "Group Inbox"
      : VIEW_TITLES[viewMode];

  return (
    <div className="flex h-full min-h-0">
      <TicketInboxNav
        viewMode={viewMode}
        activeGroupSlug={activeGroupSlug}
        onSelectView={handleSelectView}
        onSelectGroup={handleSelectGroup}
        onSelectGroupInbox={handleSelectGroupInbox}
        onSearchAll={handleSearchAll}
        onCreate={() => setShowCreateModal(true)}
      />
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        {/* Header */}
        <div className="h-12 shrink-0 border-b border-grey-200 px-4">
          {searchOpen ? (
            <div
              className="flex h-full items-center gap-3"
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  closeSearch();
                }
              }}
            >
              <SearchInput
                containerClassName="flex-1 w-full!"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onClear={() => setSearchText("")}
                placeholder="Search"
                autoFocus
              />
              <Button variant={ButtonVariant.GHOST} onClick={closeSearch} className="min-w-auto shrink-0 pr-0 pl-0">
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex h-full items-center justify-between">
              <span className="heading-base-alt truncate text-black">{activeTitle}</span>

              {/* Search */}
              <button
                onClick={() => setSearchOpen(true)}
                className={filterClasses(
                  "flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded transition",
                  searchText ? "text-orange-500" : "text-grey-900 hover:text-orange-500"
                )}
                type="button"
                title="Search"
              >
                <HiOutlineSearch className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Status + Order controls (hidden while searching) */}
        {!searchOpen && (
          <div className="shrink-0 py-2">
            <TicketListControls />
          </div>
        )}

        {/* Error state */}
        {error && <div className="mx-4 mb-3 rounded bg-red-50 p-3 text-sm text-red-600">{error}</div>}

        {/* Ticket list */}
        <div ref={scrollContainerRef} className="min-h-0 flex-1 space-y-1 overflow-x-hidden overflow-y-auto px-1.5">
          {/* Initial loading state */}
          {(loading || awaitingMyGroups) && rows.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-grey-600">Loading tickets...</div>
            </div>
          )}

          {/* Empty state */}
          {!loading && !awaitingMyGroups && rows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <svg className="mb-3 h-12 w-12 text-grey-600" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M26.9995 17.0039L16.9995 27.0039" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16.9995 17.0039L26.9995 27.0039" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M22 38C30.8366 38 38 30.8366 38 22C38 13.1634 30.8366 6 22 6C13.1634 6 6 13.1634 6 22C6 30.8366 13.1634 38 22 38Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M41.9999 41.9984L33.3999 33.3984" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="body-sm text-grey-600">No Ticket Found</p>
              {statusFilterActive && (
                <button onClick={clearStatusFilter} className="mt-2 cursor-pointer text-sm text-grey-600 underline hover:text-black" type="button">
                  Clear status filter
                </button>
              )}
            </div>
          )}

          {/* Ticket items — My Inbox and Group Inbox both split into an
              "Available to accept" section (pending group tickets I can claim)
              and a section for the member's own tickets ("Assigned to me" /
              "Accepted"). My Inbox leads with the member's own tickets so their
              assigned work isn't buried under a long accept-able list; Group
              Inbox keeps "Available to accept" on top, since claiming is the
              primary action there. All Tickets and Search are flat lists. */}
          {(() => {
            const renderItem = (ticket: TicketPreview): React.JSX.Element => (
              <TicketListItem
                key={ticket.id}
                ticket={ticket}
                isSelected={selectedId === ticket.id}
                onClick={() => navigate(`/inbox/ticket/${formatTicketNumberDisplay(ticket.number)}`)}
                onAccept={() => void handleAcceptFromList(ticket)}
              />
            );

            if (viewMode !== "my" && viewMode !== "group") return rows.map(renderItem);

            // Fallback safety net: a ticket with an individual assignee is never
            // "available to accept," even if escalationState is somehow still
            // "open-to-group" (e.g. a code path that sets an assignee without
            // transitioning the state). assigneeMemberIds is the source of truth
            // for "does this have an owner."
            const isAcceptable = (r: TicketPreview): boolean => r.escalationState === "open-to-group" && (r.assigneeMemberIds?.length ?? 0) === 0;
            const acceptable = rows.filter(isAcceptable);
            const rest = rows.filter((r) => !isAcceptable(r));
            const restLabel = viewMode === "my" ? "Assigned to me" : "Accepted";
            // topPad "pt-4" (16px) + the list's space-y-1 (4px) = 20px above the label.
            const sectionLabel = (text: string, topPad = "pt-2"): React.JSX.Element => (
              <div className={filterClasses("body-sm-alt px-2.5 pb-1 text-grey-600", topPad)}>{text}</div>
            );
            // The second section only gets the larger 20px gap when it actually
            // sits below a first section; when the first section is empty it
            // uses the normal first-label padding, matching every other inbox's
            // spacing under the filters.

            // My Inbox leads with the member's own tickets so their assigned
            // work isn't buried under a long accept-able list; Group Inbox keeps
            // "Available to accept" on top, since claiming is the primary action.
            if (viewMode === "my") {
              return (
                <>
                  {rest.length > 0 && sectionLabel(restLabel)}
                  {rest.map(renderItem)}
                  {acceptable.length > 0 && sectionLabel("Available to accept", rest.length > 0 ? "pt-4" : "pt-2")}
                  {acceptable.map(renderItem)}
                </>
              );
            }
            return (
              <>
                {acceptable.length > 0 && sectionLabel("Available to accept")}
                {acceptable.map(renderItem)}
                {rest.length > 0 && sectionLabel(restLabel, acceptable.length > 0 ? "pt-4" : "pt-2")}
                {rest.map(renderItem)}
              </>
            );
          })()}

          {/* Load more sentinel */}
          {rows.length > 0 && (
            <div ref={loadMoreRef} className="flex h-8 items-center justify-center">
              {loading && <div className="text-xs text-grey-600">Loading more...</div>}
            </div>
          )}
        </div>
      </div>

      {/* Create ticket modal / action sheet */}
      {showCreateModal && isDesktop && <CreateTicketModal onClose={() => setShowCreateModal(false)} onCreated={handleTicketCreated} />}
      {!isDesktop && <CreateTicketActionSheet isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />}
    </div>
  );
}
